import React, { useState, useEffect } from 'react';
import PageHeading from '../../components/PageHeading';
import {
  VideoCameraOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import { Table, Input, message, Tag, Empty, Typography, Button, Space, Tooltip } from 'antd';
import axiosInstance from '../../utils/axios';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

const EventShowList = () => {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchShows(pagination.current, search);
  }, [pagination.current, search]);

  const fetchShows = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      // Backend event-scoped shows endpoint (assumed /news/event-shows/)
      let url = `news_and_events/event-shows/?page=${page}&page_size=${pagination.pageSize}`;
      if (searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await axiosInstance.get(url);

      const allShows = response.data.results || [];
      setShows(allShows);
      setPagination(prev => ({
        ...prev,
        current: page,
        total: response.data.count || 0,
      }));
    } catch (error) {
      message.error('Failed to load event shows');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Event',
      dataIndex: 'heading',
      key: 'heading',
      render: heading => <strong>{heading || '—'}</strong>,
    },
    {
      title: 'Venue',
      dataIndex: 'venue',
      key: 'venue',
      render: venue => venue || '—',
    },
    {
      title: 'Dates',
      key: 'dates',
      render: (_, record) => `${new Date(record.start_date).toLocaleDateString('en-IN')} to ${new Date(record.end_date).toLocaleDateString('en-IN')}`,
    },
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
    },
    {
      title: 'Status',
      key: 'publish',
      render: publish => (
        <Tag color={publish ? 'green' : 'default'}>
          {publish ? 'Published' : 'Draft'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Bookings">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() =>
                navigate(
                  `/event/shows/${record.id}`, {
                  state: { eventName: record.heading }
                }
                )
              }
            />
          </Tooltip>
          <Tooltip title="View Ticket Snapshots">
            <Button
              type="link"
              icon={<CameraOutlined />}
              style={{ color: '#1890ff' }}
              onClick={() =>
                navigate(
                  `/event/snapshots/${record.id}`, {
                  state: { eventName: record.heading }
                }
                )
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="dashboard">
      <PageHeading name="Event Show List" icon={<VideoCameraOutlined />} />

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Search by movie name"
          prefix={<SearchOutlined />}
          style={{ width: '240px' }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
        />

        <Text type="secondary">
          Active shows for <strong>your event</strong>
        </Text>
      </div>

      <Table
        dataSource={shows}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (page) => {
            setPagination(prev => ({ ...prev, current: page }));
          },
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No active shows for this event"
            />
          ),
        }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default EventShowList;

